import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile';
import { AppShell, Button } from '@dj/ui';
import { callable } from '@dj/firebase-client';
import { useClaim } from '../state/ClaimContext';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

/**
 * Step 2 — identity, then "check your email" (Tess re-scan 3, R11).
 *
 * The details go to `requestClaimLink`, which always gives the same answer.
 * If they match a member on the provider's list, a single-use link is emailed
 * to the address the PROVIDER has on file; the claim continues from that link
 * (ClaimLinkPage). Nothing on this page can tell whether a person is on a list.
 *
 * Original notes: We collect the fields the existing `claimEntitlement`
 * CF uses to roster-match: firstName + lastName + dob + optional NHI + email.
 *
 * Why NHI is optional: providers MAY upload roster rows with or without
 * NHI. When present NHI is the preferred match (one-shot national); when
 * absent we fall back to name+dob exact match. The portal asks but doesn't
 * require — if the user doesn't know their NHI we still try name+dob.
 *
 * Apple Hide-My-Email decision: the email here goes to the entitlement +
 * the linkable email on the Firebase Auth account. Footnote tells the user
 * to use the same email at app sign-in so the entitlement attaches; the
 * 'block' vs 'relink' feature flag (set in @dj/types DEFAULT_FLAGS) is
 * enforced in the RN app at sign-in time, not here.
 */
export function IdentityPage() {
  const navigate = useNavigate();
  const { provider, providerCode, identity, setIdentity } = useClaim();
  const [busy, setBusy] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance | null>(null);

  useEffect(() => {
    if (!provider) navigate('/', { replace: true });
  }, [provider, navigate]);

  const [firstName, setFirstName] = useState(identity?.firstName ?? '');
  const [lastName, setLastName] = useState(identity?.lastName ?? '');
  const [dob, setDob] = useState(identity?.dob ?? '');
  const [nhi, setNhi] = useState(identity?.nhi ?? '');
  const [email, setEmail] = useState(identity?.email ?? '');
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if (!firstName.trim()) { setError('Please enter your first name.'); return; }
    if (!lastName.trim()) { setError('Please enter your last name.'); return; }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dob)) {
      setError('Please enter your date of birth.'); return;
    }
    const cleanNhi = nhi.trim().toUpperCase();
    if (cleanNhi && !/^[A-Z]{3}\d{4}$/.test(cleanNhi)) {
      setError('NHI should be 3 letters followed by 4 digits, e.g. CXG9651.'); return;
    }
    if (email.trim() && !/.+@.+\..+/.test(email)) { setError('Please check your email address.'); return; }
    if (TURNSTILE_SITE_KEY && !turnstileToken) { setError('Please complete the verification challenge.'); return; }

    const details = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      dob,
      nhi: cleanNhi || undefined,
      email: email.trim().toLowerCase(),
    };
    setIdentity(details);
    setBusy(true);
    try {
      const ask = callable<any, { success: boolean; reason?: string }>('requestClaimLink');
      const r = await ask({ code: providerCode, ...details, email: details.email || undefined, turnstileToken: turnstileToken ?? undefined });
      if (r.reason === 'rate_limited' || r.reason === 'captcha_failed') {
        turnstileRef.current?.reset();
        setTurnstileToken(null);
        setError(r.reason === 'rate_limited' ? 'Too many attempts — please wait a while and try again.' : 'Verification failed — please try again.');
        return;
      }
      navigate('/check-email');
    } catch (e: any) {
      setError("We couldn't send that right now. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!provider) return null;

  return (
    <AppShell title="Your details">
      <div className="bg-white rounded-lg shadow p-6 sm:p-8 max-w-lg mx-auto">
        <h1 className="text-2xl font-semibold text-slate-900 mb-1">Your details</h1>
        <p className="text-slate-600 mb-6">
          Sponsored by <span className="font-semibold">{provider.providerName}</span>. We use
          these details to find you on the provider's list, then email you a link to finish.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="First name" id="firstName">
            <input
              id="firstName"
              type="text"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Last name" id="lastName">
            <input
              id="lastName"
              type="text"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className={inputCls}
            />
          </Field>
        </div>

        <Field label="Date of birth" id="dob">
          <input
            id="dob"
            type="date"
            autoComplete="bday"
            value={dob}
            onChange={(e) => setDob(e.target.value)}
            max={new Date().toISOString().slice(0, 10)}
            className={inputCls}
          />
        </Field>

        <Field label="NHI number (optional)" id="nhi">
          <input
            id="nhi"
            type="text"
            autoCapitalize="characters"
            spellCheck={false}
            value={nhi}
            onChange={(e) => setNhi(e.target.value.toUpperCase())}
            placeholder="e.g. CXG9651"
            className={`${inputCls} font-mono tracking-wider uppercase`}
            maxLength={7}
          />
          <p className="text-xs text-slate-500 mt-1">
            Helps us find you faster. Skip if you don't know it.
          </p>
        </Field>

        <Field label="Email address (optional)" id="email">
          <input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputCls}
          />
          <p className="text-xs text-slate-500 mt-1">
            Only helps us find you if your provider didn't record your date of birth.
            Your link always goes to the email your provider has for you.
          </p>
        </Field>

        {TURNSTILE_SITE_KEY && (
          <div className="mb-3">
            <Turnstile
              ref={turnstileRef}
              siteKey={TURNSTILE_SITE_KEY}
              onSuccess={(token) => setTurnstileToken(token)}
              onExpire={() => setTurnstileToken(null)}
              onError={() => setTurnstileToken(null)}
              options={{ theme: 'light', size: 'flexible' }}
            />
          </div>
        )}

        {error && <div className="bg-red-50 text-red-700 text-sm p-3 rounded mb-3">{error}</div>}

        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => navigate('/')}>Back</Button>
          <Button variant="primary" fullWidth onClick={submit} disabled={busy}>
            {busy ? 'Sending…' : 'Email me a link'}
          </Button>
        </div>
      </div>
    </AppShell>
  );
}

const inputCls =
  'w-full border border-slate-300 rounded-md px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-teal-700 focus:border-teal-700';

function Field({ label, id, children }: { label: string; id: string; children: any }) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor={id}>
        {label}
      </label>
      {children}
    </div>
  );
}
